export interface UserInfo {
  id: string;
  name: string;
  email: string;
}

export interface Member {
  id: string;
  name: string;
  email?: string;
}

export type ProjectAccess = 'owner' | 'member' | 'share';

export type ProjectVisibility = 'private' | 'public';

export interface ProjectSummary {
  id: string;
  name: string;
  description: string;
  visibility?: ProjectVisibility;
  memberCount: number;
  taskCount: number;
  access?: ProjectAccess;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectDetail {
  id: string;
  name: string;
  description: string;
  visibility?: ProjectVisibility;
  members: Member[];
  access?: ProjectAccess;
  createdAt: string;
  updatedAt: string;
}

export interface PublicProject {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export interface TaskComment {
  id: string;
  authorId: string;
  authorName: string;
  body: string;
  createdAt: string;
}

export type TaskStatus =
  | 'not_started'
  | 'started'
  | 'in_progress'
  | 'blocked'
  | 'complete';

export interface TaskItem {
  id: string;
  project: string;
  title: string;
  description: string;
  status: TaskStatus;
  storyPoints: number;
  assigneeId: string | null;
  assigneeName: string;
  epicId: string | null;
  parentId: string | null;
  sprintId: string | null;
  completedAt: string | null;
  comments?: TaskComment[];
  createdAt: string;
  updatedAt: string;
}

export interface Epic {
  id: string;
  project: string;
  name: string;
  description: string;
  comments?: TaskComment[];
  createdAt: string;
  updatedAt: string;
}

export interface Sprint {
  id: string;
  project: string;
  name: string;
  startDate: string;
  endDate: string | null;
  active: boolean;
  taskCount: number;
  completedCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ReportMember {
  name: string;
  points: number;
  count: number;
}

export interface ReportData {
  range: string;
  totalPoints: number;
  totalCount: number;
  members: ReportMember[];
  statusBreakdown: { _id: string; count: number; points: number }[];
}

const BASE = '/api';

let token: string | null = localStorage.getItem('flowboard_token');
let shareToken: string | null = null;

export function getToken(): string | null {
  return token;
}

export function setToken(t: string | null): void {
  token = t;
  if (t) localStorage.setItem('flowboard_token', t);
  else localStorage.removeItem('flowboard_token');
}

export function setShareToken(t: string | null): void {
  shareToken = t;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (shareToken) {
    // Share-token requests are view-only by convention; do not also send the
    // owner JWT so the server always enforces read-only access for a shared link.
    headers['x-share-token'] = shareToken;
  } else if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(BASE + path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError((data && (data as any).error) || 'Request failed', res.status);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};
